import { useAuth0 } from '@auth0/auth0-react';
import type { Private } from '@seek/indie-api-types';
import {
  Actions,
  Badge,
  Box,
  ButtonIcon,
  IconDelete,
  IconProfile,
  IconSent,
  Inline,
  TableCell,
  TableRow,
  TooltipRenderer,
  Text,
  Stack,
} from 'braid-design-system';
import { differenceInHours, differenceInMinutes } from 'date-fns';
import { useState } from 'react';

import { useOrganizations } from '../OrganizationContext.tsx';

import { DialogWithMessage } from '#src/components/DialogWithMessage/DialogWithMessage.tsx';
import { useLogin } from '#src/hooks/auth.tsx';
import { roleDisplayNames } from '#src/types/users.ts';

interface OrganizationRowProps {
  organization: Private.Auth0UserOrganizationPayload;
  isCurrentPartner: boolean;
}

type Role = Private.Auth0UserRoleChangeInput['role'];

const getRoleTone = (role: Role) => {
  switch (role) {
    case 'admin':
      return 'positive';
    case 'edit':
      return 'caution';
    case 'read-only':
      return 'info';
  }
};

export const OrganizationRow = ({
  organization,
  isCurrentPartner,
}: OrganizationRowProps) => {
  const { logout } = useAuth0();
  const { login } = useLogin();
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const { removeSelf } = useOrganizations();
  return (
    <TableRow>
      <TableCell>
        <Inline space="xsmall">
          <Text>{organization.name}</Text>
          {organization.impersonationExpiry ? (
            <TooltipRenderer
              id={`organization-${organization.id}-impersonation-tooltip`}
              tooltip={
                <Text size="xsmall">
                  {getExpiryString(organization.impersonationExpiry)}
                </Text>
              }
            >
              {({ triggerProps }) => (
                <Box aria-label="Help" {...triggerProps}>
                  <Badge>Impersonating</Badge>
                </Box>
              )}
            </TooltipRenderer>
          ) : null}
        </Inline>
      </TableCell>
      <TableCell>
        <Badge tone={getRoleTone(organization.role)}>
          {roleDisplayNames[organization.role]}
        </Badge>
      </TableCell>
      <TableCell align="right">
        <Actions>
          {isCurrentPartner ? (
            <ButtonIcon
              icon={<IconSent />}
              id={`${organization.id}-logout-org-button-icon`}
              label={`Log out of ${organization.name}`}
              onClick={async (e) => {
                e.stopPropagation();
                await logout({
                  logoutParams: {
                    returnTo: window.location.origin,
                  },
                });
              }}
            />
          ) : (
            <ButtonIcon
              icon={<IconProfile />}
              id={`${organization.id}-switch-org-button-icon`}
              label={`Switch to ${organization.name}`}
              onClick={async () => {
                await login({
                  organization: organization.id,
                });
              }}
            />
          )}
          <ButtonIcon
            icon={<IconDelete />}
            id={`${organization.id}-leave-org-button-icon`}
            label={`Leave ${organization.name}`}
            onClick={() => {
              setShowLeaveDialog(true);
            }}
          />
        </Actions>
      </TableCell>
      <DialogWithMessage
        title="Leave organization"
        id={`${organization.id}-leave-org-dialog`}
        message={
          <Stack space="gutter">
            <Text weight="strong">
              Are you sure you want to leave {organization.name}?
            </Text>
            <Text>
              This means you will no longer be able to configure{' '}
              {organization.name} via the SEEK Developer Dashboard.
            </Text>
          </Stack>
        }
        action="Leave"
        actionLoading={false}
        showDialog={showLeaveDialog}
        onCancel={() => setShowLeaveDialog(false)}
        onConfirm={async () => {
          await removeSelf(organization.id);
          if (isCurrentPartner) {
            await logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });
          }
          setShowLeaveDialog(false);
        }}
      />
    </TableRow>
  );
};

const getExpiryString = (expiryTime: string) => {
  const now = new Date();
  const expiry = new Date(expiryTime);

  const hours = differenceInHours(expiry, now);
  const minutes = differenceInMinutes(expiry, now) % 60;

  if (hours > 0 && minutes > 0) {
    return `Expires in ${hours} hours and ${minutes} mins`;
  }
  if (hours > 0) {
    return `Expires in ${hours} hours`;
  }
  return `Expires in ${minutes} mins`;
};
